import searchlineLogo from '@/assets/searchline-logo.jpg'
/**
 * CandidatePreScreen — Public magic-link pre-screen page
 *
 * Accessed via /prescreen/:token — no auth required.
 * Loads questions from the token, collects candidate responses, submits.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Loader2, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react'

const API_BASE = import.meta.env.VITE_ENGINE_API_BASE_URL ?? ''

interface TokenPayload {
  candidateId:              string
  projectId:                string
  candidateName:            string
  questions:                string[]
  // Optional context from /start — gracefully absent if not returned
  projectTitle?:            string
  roleTitle?:               string
  companyName?:             string
  clientName?:              string
  // CP-13: two-mode Erica — full pre-screen vs express interest
  hasCompletedCareerCoach?: boolean
}

// ── Shared layout shell ───────────────────────────────────────────────────────
function PageShell({ children, centred = false }: { children: React.ReactNode; centred?: boolean }) {
  return (
    <div className={`min-h-screen bg-[#02182B] flex flex-col ${centred ? 'items-center justify-center' : ''}`}>
      {children}
    </div>
  )
}

// ── Searchline wordmark ───────────────────────────────────────────────────────
function Wordmark({ size = 'md', centred = false }: { size?: 'sm' | 'md' | 'lg'; centred?: boolean }) {
  const titleCls = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-base' : 'text-lg'
  const subCls   = size === 'lg' ? 'text-sm'  : 'text-xs'
  const imgCls   = size === 'lg' ? 'w-10 h-10 rounded-xl' : size === 'sm' ? 'w-6 h-6 rounded-md' : 'w-8 h-8 rounded-lg'
  return (
    <div className={`flex items-center gap-2 ${centred ? 'justify-center' : ''}`}>
      <img src={searchlineLogo} alt="Searchline" className={imgCls} />
      <div className={centred ? 'text-center' : ''}>
        <span className={`text-white font-bold tracking-tight ${titleCls}`}>Searchline</span>
        <span className={`block text-[#FD802E] font-medium ${subCls}`}>AI Hiring</span>
      </div>
    </div>
  )
}

// ── Erica glowing circle ──────────────────────────────────────────────────────
function EricaCircle({ isActive = false }: { isActive?: boolean }) {
  const barConfigs = [
    { keyframe: 'erica-bar-1', delay: '0s',     minH: 5,  maxH: 22 },
    { keyframe: 'erica-bar-2', delay: '0.1s',   minH: 8,  maxH: 28 },
    { keyframe: 'erica-bar-3', delay: '0.05s',  minH: 6,  maxH: 32 },
    { keyframe: 'erica-bar-4', delay: '0.15s',  minH: 8,  maxH: 24 },
    { keyframe: 'erica-bar-5', delay: '0.08s',  minH: 5,  maxH: 18 },
  ]

  const barStyle = (cfg: typeof barConfigs[0], index: number): React.CSSProperties =>
    isActive
      ? { animationName: cfg.keyframe, animationDuration: ['0.8s','0.6s','0.7s','0.9s','0.75s'][index], animationTimingFunction: 'ease-in-out', animationIterationCount: 'infinite', animationDelay: cfg.delay, height: `${cfg.minH}px` }
      : { height: `${cfg.minH}px` }

  return (
    <>
      <style>{`
        @keyframes erica-bar-1 { 0%,100% { height: 5px; }  50% { height: 22px; } }
        @keyframes erica-bar-2 { 0%,100% { height: 8px; }  50% { height: 28px; } }
        @keyframes erica-bar-3 { 0%,100% { height: 6px; }  50% { height: 32px; } }
        @keyframes erica-bar-4 { 0%,100% { height: 8px; }  50% { height: 24px; } }
        @keyframes erica-bar-5 { 0%,100% { height: 5px; }  50% { height: 18px; } }
      `}</style>
      <div className="relative flex items-center justify-center w-32 h-32">
        {/* Outer ring — pulses, brighter when active */}
        <div className={`absolute inset-0 rounded-full border-4 animate-pulse transition-all duration-700 ${
          isActive
            ? 'bg-[#FD802E]/30 border-[#FD802E]/70 shadow-[0_0_32px_rgba(253,128,46,0.45)]'
            : 'bg-[#FD802E]/15 border-[#FD802E]/30'
        }`} />
        {/* Middle ring */}
        <div className={`absolute w-24 h-24 rounded-full border-2 transition-all duration-700 ${
          isActive
            ? 'bg-[#FD802E]/40 border-[#FD802E]/80'
            : 'bg-[#FD802E]/20 border-[#FD802E]/50'
        }`} />
        {/* Inner circle — waveform bars */}
        <div className={`relative w-16 h-16 rounded-full flex items-center justify-center transition-all duration-700 ${
          isActive ? 'bg-[#02182B]' : 'bg-[#02182B]'
        }`}>
          <div className="flex items-center gap-[3px]">
            {barConfigs.map((cfg, i) => (
              <div
                key={i}
                className="w-[4px] rounded-full bg-[#FD802E] transition-all"
                style={barStyle(cfg, i)}
              />
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

export function CandidatePreScreen() {
  const { token } = useParams<{ token: string }>()

  const [payload,         setPayload]         = useState<TokenPayload | null>(null)
  const [loading,         setLoading]         = useState(true)
  const [invalid,         setInvalid]         = useState(false)
  const [answers,         setAnswers]         = useState<string[]>([])
  const [step,            setStep]            = useState(0)
  const [showIntro,       setShowIntro]       = useState(true)
  const [introOpacity,    setIntroOpacity]    = useState(true)   // drives intro fade-out
  const [questionsOpacity, setQuestionsOpacity] = useState(false) // drives questions fade-in
  const [submitting,      setSubmitting]      = useState(false)
  const [done,            setDone]            = useState(false)
  const [error,           setError]           = useState<string | null>(null)

  // Load token → questions
  useEffect(() => {
    if (!token) { setInvalid(true); setLoading(false); return }
    fetch(`${API_BASE}/api/prescreen/${token}/start`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((data: TokenPayload) => {
        setPayload(data)
        setAnswers(new Array(data.questions.length).fill(''))
        // CP-13: log flag — routing logic (full pre-screen vs express interest) wired in CP-13
        console.log('[CandidatePreScreen] hasCompletedCareerCoach =', data.hasCompletedCareerCoach ?? false)
        setLoading(false)
      })
      .catch(() => { setInvalid(true); setLoading(false) })
  }, [token])

  // Smooth transition: intro fades out → question flow fades in
  const handleStart = () => {
    setIntroOpacity(false)                     // begin intro fade-out
    setTimeout(() => {
      setShowIntro(false)                      // unmount intro, mount questions (opacity-0)
      setTimeout(() => setQuestionsOpacity(true), 50) // trigger question fade-in after render
    }, 500)                                    // match duration-500
  }

  const handleSubmit = async () => {
    if (!payload || !token) return
    setSubmitting(true)
    setError(null)
    try {
      const responses = payload.questions.map((q, i) => ({ question: q, answer: answers[i] ?? '' }))
      const res = await fetch(`${API_BASE}/api/prescreen/${token}/submit`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ responses }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setError((body as { error?: string }).error ?? 'Submission failed. Please try again.')
        setSubmitting(false)
        return
      }
      setDone(true)
    } catch {
      setError('Network error — please check your connection and try again.')
      setSubmitting(false)
    }
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <PageShell centred>
        <div className="flex flex-col items-center gap-8 px-6">
          <Wordmark size="lg" centred />
          <Loader2 size={32} className="text-[#FD802E] animate-spin" />
          <p className="text-gray-400 text-sm tracking-wide">Loading your pre-screen session…</p>
        </div>
      </PageShell>
    )
  }

  // ── Invalid / expired token ──────────────────────────────────────────────────
  if (invalid || !payload) {
    return (
      <PageShell centred>
        <div className="w-full max-w-sm mx-auto px-6 flex flex-col items-center gap-6 text-center">
          <Wordmark size="lg" centred />
          <div className="w-12 h-12 rounded-full bg-[#233D4C] flex items-center justify-center">
            <AlertCircle size={24} className="text-[#FD802E]" />
          </div>
          <div className="space-y-3">
            <h1 className="text-white text-xl font-semibold">This link has expired or is no longer valid.</h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              Pre-screen links are valid for 7 days. If you believe this is an error, please contact
              the recruiter who sent you this invitation.
            </p>
          </div>
        </div>
      </PageShell>
    )
  }

  const firstName  = payload.candidateName.split(' ')[0]
  const roleLabel  = payload.roleTitle ?? payload.projectTitle ?? 'this role'
  const companyLabel = payload.companyName ?? payload.clientName ?? 'the hiring team'

  // ── Done ─────────────────────────────────────────────────────────────────────
  if (done) {
    return (
      <PageShell centred>
        <div className="w-full max-w-[640px] mx-auto px-6 py-12 flex flex-col items-center gap-8 text-center">
          {/* Success icon */}
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
            <CheckCircle2 size={40} className="text-green-400" />
          </div>

          {/* Confirmation copy */}
          <div className="space-y-3">
            <h1 className="text-white text-2xl font-bold">Thank you, {firstName}</h1>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
              Your responses have been submitted. Our team will review your pre-screen and be in touch with next steps.
            </p>
          </div>

          {/* Divider */}
          <div className="w-full border-t border-[#233D4C]" />

          {/* CTA */}
          <div className="w-full space-y-4">
            <p className="text-white text-base font-medium">
              Want to increase your chances of landing your next role?
            </p>
            <a
              href="https://searchline.io/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3.5 bg-[#FD802E] hover:bg-[#ff8f45] text-[#02182B] font-bold text-sm rounded-xl transition-colors text-center"
            >
              Create your Searchline profile →
            </a>
            <p className="text-gray-500 text-xs leading-relaxed">
              Candidates with a complete profile are 8× more likely to be matched to relevant opportunities.
            </p>
          </div>
        </div>
      </PageShell>
    )
  }

  const currentQ = payload.questions[step]
  const isLast   = step === payload.questions.length - 1
  const progress = ((step + 1) / payload.questions.length) * 100

  // ── Intro screen ──────────────────────────────────────────────────────────────
  if (showIntro) {
    return (
      <PageShell centred>
        <div
          className={`transition-opacity duration-500 ${introOpacity ? 'opacity-100' : 'opacity-0'} w-full max-w-lg mx-auto px-6 py-10 flex flex-col items-center gap-8`}
        >
          {/* Wordmark */}
          <Wordmark size="lg" centred />

          {/* Hero card */}
          <div className="w-full bg-[#1a3347] rounded-2xl p-8 flex flex-col items-center gap-5 text-center">
            <EricaCircle isActive={true} />
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-white">Hi {firstName}, I'm Erica</h1>
              <p className="text-sm text-gray-400">AI Recruitment Assistant · Searchline</p>
            </div>
          </div>

          {/* Intro text */}
          <p className="text-center text-white font-medium max-w-md mx-auto leading-relaxed">
            I'll be guiding you through a short pre-screen conversation today. It takes around
            10–15 minutes and you can complete it right now.
          </p>

          {/* Role pill */}
          <div className="bg-[#233D4C] border border-[#2a4a5c] text-white text-sm px-4 py-2 rounded-full mx-auto w-fit">
            You're being considered for: <span className="font-semibold">{roleLabel}</span>
            {' '}at <span className="font-semibold">{companyLabel}</span>
          </div>

          {/* CTA */}
          <button
            onClick={handleStart}
            className="w-full max-w-sm bg-[#FD802E] hover:bg-[#ff8f45] text-white font-semibold py-4 rounded-xl text-lg transition-colors"
          >
            Let's get started →
          </button>

          {/* Footer */}
          <p className="text-gray-500 text-xs text-center">Confidential · For recruitment purposes only</p>
        </div>
      </PageShell>
    )
  }

  // ── Pre-screen question flow ──────────────────────────────────────────────────
  return (
    <div
      className={`transition-opacity duration-500 ${questionsOpacity ? 'opacity-100' : 'opacity-0'} min-h-screen bg-[#02182B] flex flex-col`}
    >
      {/* ── Header ── */}
      <header className="border-b border-[#233D4C] px-6 py-4 flex items-center justify-between">
        <Wordmark />
        <p className="text-gray-500 text-xs hidden sm:block">Confidential — for recruitment purposes only</p>
      </header>

      {/* ── Progress bar ── */}
      <div className="h-1 bg-[#233D4C]">
        <div
          className="h-full bg-[#FD802E] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* ── Main content ── */}
      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-14">
        <div className="w-full max-w-[640px] space-y-6">

          {/* Question number indicator */}
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wider">
            Question {step + 1} of {payload.questions.length}
          </p>

          {/* Question text */}
          <p className="text-white text-xl font-medium leading-relaxed">{currentQ}</p>

          {/* Textarea */}
          <textarea
            key={step}
            value={answers[step] ?? ''}
            onChange={(e) =>
              setAnswers((prev) => { const n = [...prev]; n[step] = e.target.value; return n })
            }
            rows={6}
            placeholder="Type your answer here…"
            className="w-full bg-[#233D4C] border border-[#2a4a5c] rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-gray-500 focus:outline-none focus:border-[#FD802E]/60 focus:ring-1 focus:ring-[#FD802E]/20 transition-colors resize-none"
          />

          {error && (
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              {error}
            </p>
          )}

          {/* Navigation */}
          <div className="flex gap-3 justify-end">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="px-5 py-3 text-sm text-gray-400 hover:text-white border border-[#2a4a5c] rounded-xl transition-colors"
              >
                Back
              </button>
            )}
            {isLast ? (
              <button
                onClick={handleSubmit}
                disabled={submitting || !(answers[step] ?? '').trim()}
                className="flex items-center gap-2 px-6 py-3 bg-[#FD802E] text-[#02182B] font-bold text-sm rounded-xl hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={14} />
                )}
                {submitting ? 'Submitting…' : 'Submit answers'}
              </button>
            ) : (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!(answers[step] ?? '').trim()}
                className="flex items-center gap-2 px-6 py-3 bg-[#FD802E] text-[#02182B] font-bold text-sm rounded-xl hover:bg-[#ff8f45] transition-colors disabled:opacity-50"
              >
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>
      </main>

      {/* ── Footer note (mobile) ── */}
      <footer className="px-6 py-4 sm:hidden text-center">
        <p className="text-gray-600 text-xs">Confidential — for recruitment purposes only</p>
      </footer>
    </div>
  )
}
